/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  Input,
  ViewChild,
  OnChanges,
  SimpleChanges,
  inject,
  computed,
} from '@angular/core'
import { MultiSelectComponent } from '@progress/kendo-angular-dropdowns'
import { FilterService } from '@progress/kendo-angular-grid'
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  FilterOperator,
} from '@progress/kendo-data-query'

import { Environment } from '@cyborg-monorepo/hunter/common'

import { SessionService } from '@hunt/services'

import { BaseCustomFilterComponent } from '../../../../../shared/components/custom-filters/base-custom-filter.component'
import { DashboardStateService } from '../../dashboard-state-service'

@Component({
  standalone: false,
  selector: 'dashboard-environment-filter',
  template: `
    <kendo-multiselect
      #multiselect
      [value]="currentFilterValue"
      [data]="data"
      [field]="field"
      (valueChange)="onChange($event)"
      [textField]="textField"
      [valueField]="valueField"
      placeholder="(All)"
    >
    </kendo-multiselect>
  `,
  styles: [
    `
      kendo-daterange > kendo-dateinput.range-filter {
        display: inline-block;
      }
      .k-input-solid {
        background-color: var(--background-overlay-default);
        border-radius: var(--Corner-Radius-Tertiary, 4px);
        border: var(--Stroke-Size-Primary, 1px) solid
          var(--border-default, #323333);
        background: var(--background-function-inset, #0c0c0c);
      }
      .k-list-item-text {
        color: red;
      }
    `,
  ],
})
export class DashboardEnvironmentFilterComponent
  extends BaseCustomFilterComponent
  implements OnChanges
{
  dashboardStateService = inject(DashboardStateService)

  readonly session = inject(SessionService)
  readonly loaded = computed(
    () => !this.session.loading() && this.session.activeHunt() !== null,
  )
  @ViewChild('multiselect', { read: MultiSelectComponent })
  public select: MultiSelectComponent

  @Input() public filter: CompositeFilterDescriptor
  @Input() public data: Environment[]
  @Input() public field: string
  @Input() public textField: string
  @Input() public valueField: string

  public cachedFilterValue: any[] = []

  // Constructor needed for proper filter functioning
  constructor() {
    const filterService = inject(FilterService, { optional: true })

    super(filterService)
  }

  // Trigger recalculation of currentFilterValue only when inputs change
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.filter || changes.data) {
      this.cachedFilterValue = this.calculateCurrentFilterValue()
    }
  }

  // Get current filter value by using the cached result
  public get currentFilterValue(): any {
    return this.cachedFilterValue
  }

  // Calculate the filter value based on filter and data
  private calculateCurrentFilterValue(): any[] {
    if (this.filter && this.filter.filters) {
      const filter = (this.filter.filters as FilterDescriptor[]).find(
        ({ field }) => field === this.field,
      )

      const result: any[] = []
      if (filter && filter.value && filter.value.length && this.data) {
        filter.value.forEach((value) => {
          const item: any[] = this.data.filter((item) => item.id === value)
          if (item && item[0]) {
            result.push(item[0])
          }
        })
      }
      return result
    }
    return []
  }

  // Reset filter logic
  public resetDefault(): void {
    this.filter.filters = this.filter.filters.filter(
      (field) => (field as FilterDescriptor).field !== this.field,
    )
  }

  // Handle value changes in the multi-select dropdown
  public onChange(value: any): void {
    const processedValue = value.map((a) => a.id)

    const filter = (this.filter.filters as FilterDescriptor[]).find(
      ({ field }) => field === this.field,
    )

    if (value === null || processedValue.length == 0) {
      this.filter.filters = this.filter.filters.filter(
        (field) => (field as FilterDescriptor).field !== this.field,
      )
    } else {
      if (filter) {
        filter.value = processedValue
      } else {
        this.filter.filters.push({
          field: this.field,
          operator: FilterOperator.LessThanOrEqual,
          value: processedValue,
        })
      }
    }
  }
}
